import { useRef, useEffect, useState } from 'react';
import type { Initiative } from '@/types';

interface InitiativePopoverProps {
  initiative: Initiative;
  anchorRect: { top: number; left: number; width: number; height: number };
  onClose: () => void;
}

export function InitiativePopover({ initiative, anchorRect, onClose }: InitiativePopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    // Position popover below the card, centered horizontally
    const popoverWidth = 320;
    let left = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
    let top = anchorRect.top + anchorRect.height + 8;

    // Keep within viewport
    if (left < 16) left = 16;
    if (left + popoverWidth > window.innerWidth - 16) left = window.innerWidth - popoverWidth - 16;
    if (top + 200 > window.innerHeight) {
      top = anchorRect.top - 8 - 200; // flip above
    }

    setPosition({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const status = initiative.status;
  const classification = initiative.classification;
  const owner = initiative.user;
  const deps = initiative.dependencies || [];
  const tags = initiative.tags || [];

  return (
    <div
      ref={popoverRef}
      className="fixed z-[10001] w-80 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden animate-popover-in"
      style={{ top: position.top, left: position.left }}
    >
      {/* Header with classification accent */}
      <div
        className="px-4 py-3 border-b border-gray-100"
        style={{ borderLeftWidth: '4px', borderLeftColor: classification?.color || '#3B82F6' }}
      >
        <h3 className="text-sm font-semibold text-gray-900 leading-tight">
          {initiative.title}
        </h3>
        {initiative.subProduct && (
          <p className="text-xs text-gray-500 mt-0.5">
            {initiative.subProduct.product?.name} &rsaquo; {initiative.subProduct.name}
          </p>
        )}
      </div>

      {/* Details */}
      <div className="px-4 py-3 space-y-2">
        {/* Status */}
        {status && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Status</span>
            <span className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: status.color }}
              />
              <span className="text-xs font-medium text-gray-700">{status.name}</span>
            </span>
          </div>
        )}

        {/* Classification */}
        {classification && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Classification</span>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: `${classification.color}15`,
                color: classification.color,
              }}
            >
              {classification.badgeLabel}
            </span>
          </div>
        )}

        {/* Owner */}
        {owner && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Owner</span>
            <span className="text-xs font-medium text-gray-700">{owner.name}</span>
          </div>
        )}

        {/* Timeline */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Timeline</span>
          <span className="text-xs font-medium text-gray-700">
            {initiative.startQuarter?.replace('_', ' ')}
            {initiative.endQuarter && initiative.endQuarter !== initiative.startQuarter && (
              <> → {initiative.endQuarter.replace('_', ' ')}</>
            )}
          </span>
        </div>

        {/* Dependencies */}
        {deps.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Dependencies</span>
            <span className="text-xs font-medium text-gray-700">
              {deps.length} linked
            </span>
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="pt-1">
            <div className="flex flex-wrap gap-1">
              {tags.map((t) => (
                <span
                  key={t.tagId}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                >
                  {t.tag?.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-[10px] text-gray-400 text-center">Click anywhere to dismiss</p>
      </div>
    </div>
  );
}
